import React, {useCallback, useContext} from "react"
import {useParams} from "react-router-dom"
import KeyValueStore from "orbit-db-kvstore"
import {DeleteRepository} from "../../../components/Settings/DeleteRepository"
import {Description} from "../../../components/Settings/Description"
import {MemberAccess} from "../../../components/Settings/MemberAccess"
import {RepoName} from "../../../components/Settings/RepoName"
import {RepoTags} from "../../../components/Settings/RepoTags"
import {BackendContext} from "../../../context/BackendContext"
import {ProfileContext} from "../../../context/Profile.context"
import {UIHelperContext} from "../../../context/UIHelper.context"
import {useNavigate} from "react-router-dom"
import {notifyUpdate} from "../../../functions/ipfs-orbit"
import {RepoDB} from "../../../models/RepoDB.model"
import styles from "./index.module.scss"

interface SettingsInterface {
  repoDB?: RepoDB
}

export const Settings = (props: SettingsInterface) => {
  const {repoDB} = props
  const {ipfs} = useContext(BackendContext)
  const {profile} = useContext(ProfileContext)
  const {setSnackMessage} = useContext(UIHelperContext)
  const {orbitId} = useParams()
  const navigate = useNavigate()
  const repoKey = `repo_${orbitId}`

  const collaborators = repoDB?.get("collaborators") || []

  // These are all handler factories to cater for different user interactions in the Index Page
  // It is designed so such that `profile` and `repoDB` are certainly defined when calling the handlers
  const onDescriptionSave = useCallback(async (description: string) => {
    if (!repoDB || !ipfs) {
      return
    }
    await repoDB.set("description", description)
    await notifyUpdate(ipfs, repoDB.address.toString())
    setSnackMessage("Description is updated. The changes will be reflected after reload.")
  }, [ipfs, repoDB])

  const onTagsChanged = (db: RepoDB) => async (tags: string[]) => {
    await db.set("tags", tags)
  }

  const onRepoDel = (db: KeyValueStore<any>) => async () => {
    await db.del(repoKey)
    navigate("/repos/")
  }

  /**
   * This handler will change:
   * - The `name` field under database holding repository info
   * - The `name` subfield under database holding profile info and a shallow copy of Index info
   *
   * This handler will not change:
   * - The address of any (repository) database
   * - The repository ID (`repoId`)
   */
  const onRepoNameSave = (profile: KeyValueStore<any>, repo: RepoDB) => async (name: string) => {
    const repoData = profile.get(repoKey)
    await profile.set(repoKey, Object.assign(repoData, {name}))
    await repo.set("name", name)
    setSnackMessage("Repository name is updated. The changes will be reflected after reload.")
  }

  if (!profile || !repoDB) {
    return null
  }

  return (
    <div className={styles.Container}>
      <div className={styles.Header}>
        Repository Setting
      </div>
      <RepoName
        ownerName={profile.get("name")}
        repoId={repoDB.get("name")}
        onSave={onRepoNameSave(profile, repoDB)}
      />
      <RepoTags
        onChanged={onTagsChanged(repoDB)}
        tags={repoDB.get("tags")}
      />
      <Description
        onSave={val => onDescriptionSave(val).then()}
        value={repoDB.get("description")}
      />
      <MemberAccess collaborators={collaborators}
                    repoDB={repoDB}
      />
      <DeleteRepository
        repoId={repoDB.get("name")}
        onClick={onRepoDel(profile)}
      />
    </div>
  )
}
